import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { subDays, subWeeks, subYears } from 'date-fns';
import { Box, Grid } from '@mui/material';
import { utils, writeFileXLSX } from 'xlsx';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { toast } from 'react-toastify';

import {
  getClientReport,
  getDayWiseHourReport,
  getEmployeeReport,
  getProjectReport,
  getTaskNoteReport,
  getTaskTypeReport,
  getTimeSheetReport,
  getPlannedVsActualReport,
} from '../../services/reportService';
import { getTeamMembers } from '../../services/teamService';

import { GRAPH_LIST, INITIAL_REPORT_FILTER_STATE, INITIAL_REPORT_STATE } from './constants';
import { dateToDDMMYYY, dateToTime, getTimeZone, secondsToTime } from '../../utils/timeCalculation';
import { isAdmin, isClient, isProjectQaReportPlannedVsActual, isStakeholder } from '../../utils/share';

import { DayWiseHourReport, DoughnutReportView, TimeSheetReport } from './charts';
import { ExportActions, FilterBox, InfoSection } from './components';
import Loading from '../../layouts/loading';

import { useStyles } from './Style/reportPageStyles';
import './Style/report.css';
import GenerateChartData from './common/generateChartData';
import BreadCrumb from '../../components/breadcrumb/BreadCrumbNavigation';
import PlannedVsActualReportDataTable from './components/PlannedVsActualReport/PlannedVsActualReportDataTable';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, ChartDataLabels);

export default function ReportPage() {
  const classes = useStyles();
  /* get state data and export to XLSX */
  const componentRef = useRef();
  const location = useLocation();

  const currentUser = useSelector((state) => state?.login?.user);

  const [filter, setFilter] = useState(INITIAL_REPORT_FILTER_STATE);
  const [report, setReport] = useState(INITIAL_REPORT_STATE);

  const [totalTime, setTotalTime] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [members, setMembers] = useState();

  const handleSearchClick = (values) => {
    const newFilter = {};
    newFilter.dateRange = values?.dateRange;
    // Preserve full option objects so FilterBox can rehydrate selections
    newFilter.clientList = values?.clientList || [];
    newFilter.projectList = values?.projectList || [];
    newFilter.projectStatusList = values?.projectStatusList || [];
    newFilter.taskStatusList = values?.taskStatusList || [];
    newFilter.employeeList = values?.employeeList || [];
    newFilter.graphList = values?.graphList || [];
    newFilter.taskTypeList = values?.taskTypeList || [];
    newFilter.billableTypeList = values?.billableTypeList || [];
    newFilter.selectedClientIds = values?.clientList?.map((client) => client?.id);
    newFilter.selectedProjectIds = values?.projectList?.map((project) => project?.id);
    newFilter.selectedProjectStatusList = values?.projectStatusList;
    newFilter.selectedTaskStatusList = values?.taskStatusList;
    newFilter.selectedEmployeeIds = values?.employeeList?.map((employee) => employee?.id);
    newFilter.selectedGraphList = values?.graphList;
    newFilter.taskNote = values?.taskNote;
    newFilter.selectedTaskTypes = values?.taskTypeList;
    newFilter.selectedBillableIds = values?.billableTypeList?.map((billable) => billable?.id);
    setFilter((prev) => ({
      ...prev,
      ...newFilter,
      __dashboardApplied: prev.__dashboardApplied,
    }));
  };
  useEffect(() => {
    if (!location.state?.fromProjectDashboard || !location.state?.project) return;

    setFilter((prev) => {
      if (prev.__dashboardApplied) return prev;

      const today = new Date();
      const oneWeekAgo = subWeeks(today, 2);

      return {
        ...prev,
        dateRange: {
          selection: {
            startDate: oneWeekAgo,
            endDate: today,
            key: 'selection',
          },
        },

        selectedProjectIds: [location.state.project.id],
        selectedGraphList: ['Timesheet Report'],

        __dashboardApplied: true,
      };
    });
  }, [location.state]);

  const exportFile = async () => {
    try {
      setLoading(true);
      const timeSheetData = report?.timeSheetReport?.table ?? [];

      const data = timeSheetData?.map((item) => ({
        employee: item?.tasks?.user?.userName,
        project: item?.tasks?.projects?.projectName,
        client: item?.tasks?.projects?.clients?.clientName,
        task: item?.tasks?.taskName,
        taskType: item?.tasks?.taskType,
        start: `${dateToDDMMYYY(item?.startTime)} ${dateToTime(item?.startTime)}`,
        end: `${dateToDDMMYYY(item?.startTime)} ${item?.stopTime ? dateToTime(item?.stopTime) : 'Running'}`,
        durationInSecond: item?.duration,
        duration: secondsToTime(item?.duration),
        status: item?.tasks?.status,
        plannedInSecond: (item?.tasks?.plannedHours || 0) * 60 * 60,
        'planned-duration': (item?.tasks?.plannedHours || 0) * 60 * 60 - (item?.duration || 0),
      }));

      const ws = utils.json_to_sheet(data);
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, 'Data');
      writeFileXLSX(wb, 'report.xlsx');
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        if (!isAdmin() && !isClient() && !isStakeholder()) {
          const getTeamMembersResponse = await getTeamMembers();
          const allowedList = getTeamMembersResponse?.data?.data ?? [];
          allowedList.push(currentUser?.id);
          setMembers(allowedList);
        }
      } catch (error) {
        setLoading(false);
        toast.error(error?.response?.data?.message);
      }
    };

    fetchTeamMembers();
  }, [currentUser?.id]);

  useEffect(() => {
    let totalTime = 0;
    const effectiveProjectIds = filter?.selectedProjectIds || [];
    const fetchDailyReport = async (employeeList) => {
      const response = await getDayWiseHourReport(
        filter?.selectedClientIds,
        effectiveProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );

      setReport((prev) => ({ ...prev, dayWiseHourReport: response?.dailyHours }));
      setTotalTime(response?.totalTime);
      totalTime = response?.totalTime;
    };

    const fetchClientReport = async (employeeList) => {
      const responseData = await getClientReport(
        filter?.selectedClientIds,
        effectiveProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );
      const clientReport = {};
      clientReport.table = responseData?.clients ?? [];
      clientReport.graph =
        responseData?.clients.length > 0 ? GenerateChartData(responseData?.clients, 'clientName', totalTime, 6) : [];
      setReport((prev) => ({ ...prev, clientReport }));
    };

    const fetchProjectReport = async (employeeList) => {
      const responseData = await getProjectReport(
        filter?.selectedClientIds,
        effectiveProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );
      const projectReport = {};
      projectReport.table = responseData?.projects ?? [];
      projectReport.graph =
        responseData?.projects.length > 0 ? GenerateChartData(responseData?.projects, 'projectName', totalTime, 6) : [];
      setReport((prev) => ({ ...prev, projectReport }));
    };

    const fetchEmployeeReport = async (employeeList) => {
      const responseData = await getEmployeeReport(
        filter?.selectedClientIds,
        effectiveProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );
      const employeeReport = {};
      employeeReport.table = responseData?.users ?? [];
      employeeReport.graph =
        responseData?.users.length > 0 ? GenerateChartData(responseData?.users, 'userName', totalTime, 10) : [];
      setReport((prev) => ({ ...prev, employeeReport }));
    };

    const fetchTaskNoteReport = async (employeeList) => {
      const responseData = await getTaskNoteReport(
        filter?.selectedClientIds,
        effectiveProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );
      const taskNoteReport = {};
      taskNoteReport.table = responseData?.tasks ?? [];
      taskNoteReport.graph =
        responseData?.tasks.length > 0 ? GenerateChartData(responseData?.tasks, 'taskName', totalTime, 6) : [];
      setReport((prev) => ({ ...prev, taskNoteReport }));
    };

    const fetchTaskTypeReport = async (employeeList) => {
      const responseData = await getTaskTypeReport(
        filter?.selectedClientIds,
        effectiveProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );
      const taskTypeReport = {};
      taskTypeReport.table = responseData?.tasks ?? [];
      taskTypeReport.graph =
        responseData?.tasks.length > 0 ? GenerateChartData(responseData?.tasks, 'taskType', totalTime, 6) : [];
      setReport((prev) => ({ ...prev, taskTypeReport }));
    };

    const fetchTimeSheetReport = async (employeeList) => {
      const responseData = await getTimeSheetReport(
        filter?.selectedClientIds,
        effectiveProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );
      setReport((prev) => ({ ...prev, timeSheetReport: { table: responseData?.timeSheet } }));
    };

    const fetchPlannedVsActualReport = async (employeeList) => {
      const responseData = await getPlannedVsActualReport(
        filter?.selectedClientIds,
        effectiveProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );
      setReport((prev) => ({ ...prev, plannedVsActualReport: { table: responseData?.plannedVsActualReport } }));
    };

    const fetchReportData = async () => {
      setLoading(true);
      if (isAdmin() || isClient() || isStakeholder() || members) {
        try {
          const employeeList = filter?.selectedEmployeeIds?.length || isAdmin() ? filter?.selectedEmployeeIds : members;
          await fetchDailyReport(employeeList);

          const promises = [];

          if (filter?.selectedGraphList.length === 0 || filter?.selectedGraphList.includes('Client Report')) {
            promises.push(fetchClientReport(employeeList));
          }

          if (filter?.selectedGraphList.length === 0 || filter?.selectedGraphList.includes('Project Report')) {
            promises.push(fetchProjectReport(employeeList));
          }

          if (
            isAdmin() &&
            (filter?.selectedGraphList.length === 0 || filter?.selectedGraphList.includes('Employee Report'))
          ) {
            promises.push(fetchEmployeeReport(employeeList));
          }

          if (filter?.selectedGraphList.length === 0 || filter?.selectedGraphList.includes('Task Note Report')) {
            promises.push(fetchTaskNoteReport(employeeList));
          }

          if (filter?.selectedGraphList.length === 0 || filter?.selectedGraphList.includes('Timesheet Report')) {
            promises.push(fetchTimeSheetReport(employeeList));
          }

          if (filter?.selectedGraphList.length === 0 || filter?.selectedGraphList.includes('Task Type Report')) {
            promises.push(fetchTaskTypeReport(employeeList));
          }

          if (
            (isAdmin() || isProjectQaReportPlannedVsActual()) &&
            (filter?.selectedGraphList.length === 0 || filter?.selectedGraphList.includes('Planned vs Actual Report'))
          ) {
            promises.push(fetchPlannedVsActualReport(employeeList));
          }

          await Promise.all(promises);
        } catch (error) {
          toast.error(error?.response?.data?.message);
        } finally {
          setLoading(false);
        }
      }
    };

    if (location.state?.fromProjectDashboard && !filter.__dashboardApplied) {
      return;
    }
    fetchReportData();
  }, [filter, members]);

  const renderReportComponent = (graph) => {
    switch (graph) {
      case 'Client Report':
        return (
          <DoughnutReportView
            data={report?.clientReport}
            reportHeading="Client Report"
            tableTitle="Client"
            tableDataColumn="clientName"
          />
        );
      case 'Project Report':
        return (
          <DoughnutReportView
            data={report?.projectReport}
            reportHeading="Project Report"
            tableTitle="Project"
            tableDataColumn="projectName"
          />
        );
      case 'Employee Report':
        return isAdmin() ? (
          <DoughnutReportView
            data={report?.employeeReport}
            reportHeading="Employee Report"
            tableTitle="Employee"
            tableDataColumn="userName"
          />
        ) : null;
      case 'Task Note Report':
        return (
          <DoughnutReportView
            data={report?.taskNoteReport}
            reportHeading="Task Note Report"
            tableTitle="Task"
            tableDataColumn="taskName"
          />
        );
      case 'Timesheet Report':
        return <TimeSheetReport data={report?.timeSheetReport} />;

      case 'Planned vs Actual Report':
        console.log(filter?.dateRange);
        return (
          <PlannedVsActualReportDataTable
            data={report?.plannedVsActualReport?.table}
            startDate={filter?.dateRange?.selection?.startDate}
            endDate={filter?.dateRange?.selection?.endDate}
          />
        );
      default:
        return null;
    }
  };

  // const chartList = filter?.selectedGraphList.length > 0 ? filter?.selectedGraphList : GRAPH_LIST;
  const chartList =
    filter?.selectedGraphList && filter.selectedGraphList.length > 0 ? filter.selectedGraphList : GRAPH_LIST;

  const breadcrumbs = [
    {
      label: 'Home',
      path: '/',
    },
    {
      label: 'Report',
      path: '',
    },
  ];

  return (
    <>
      <Loading open={isLoading} />
      <Grid container>
        <Grid item xs={12}>
          <BreadCrumb breadcrumbs={breadcrumbs} />
        </Grid>

        <Grid item xs={12}>
          <FilterBox handleSearchClick={handleSearchClick} appliedFilter={filter} />
        </Grid>

        <Box className={classes.toolbar}>
          <InfoSection totalTime={totalTime} dateRange={filter?.dateRange} />
          <ExportActions graphContainerRef={componentRef} exportFile={exportFile} />
        </Box>

        <Grid container ref={componentRef} style={{ margin: 5 }}>
          {chartList.includes('Time Line Report') && <DayWiseHourReport data={report.dayWiseHourReport} />}

          {chartList.map((graph) => (
            <React.Fragment key={graph}>{renderReportComponent(graph)}</React.Fragment>
          ))}
        </Grid>
      </Grid>
    </>
  );
}
